import React from "react";
import { Button } from "@/components/ui/button";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from "@/providers/ThemeProvider";

interface CodePreviewProps {
  code: string;
  language: "csharp" | "gdscript" | "cpp" | "typescript" | "json" | "lua";
  gameName?: string;
  templateName?: string;
}

export default function CodePreview({ code, language, gameName, templateName }: CodePreviewProps) {
  const { effectiveTheme } = useTheme();
  
  // Helper to sanitize filenames
  const sanitizeFilename = (name: string) => name.replace(/[^a-zA-Z0-9-_]/g, '_').replace(/_{2,}/g, '_');
  
  // Language labels for display
  const languageLabels: Record<string, string> = {
    csharp: "C#",
    gdscript: "GDScript",
    cpp: "C++",
    typescript: "TypeScript",
    json: "JSON",
    lua: "Luau (Roblox)"
  };
  
  // File extensions for download
  const fileExtensions: Record<string, string> = {
    csharp: "cs",
    gdscript: "gd",
    cpp: "h",
    typescript: "ts",
    json: "json",
    lua: "luau"
  };
  
  const languageLabel = languageLabels[language] || "Code";
  
  // Generate filename: GameName-T-TemplateName.ext
  let fileName: string;
  if (gameName && templateName) {
    fileName = `${sanitizeFilename(gameName)}-T-${sanitizeFilename(templateName)}.${fileExtensions[language] || "txt"}`;
  } else {
    fileName = `locanest_generated_${Date.now()}.${fileExtensions[language] || "txt"}`;
  }
  
  // Map our language types to syntax highlighter languages
  // GDScript is similar to Python, so we use python for syntax highlighting
  // Lua/Luau uses lua syntax highlighting
  const syntaxLanguage = language === "gdscript" ? "python" : 
                         language === "cpp" ? "cpp" :
                         language === "typescript" ? "typescript" :
                         language === "json" ? "json" :
                         language === "lua" ? "lua" :
                         "csharp";
  
  // Select theme based on effective theme
  const syntaxTheme = effectiveTheme === "dark" ? vscDarkPlus : vs;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
  };
  
  const handleDownload = () => {
    try {
      const blob = new Blob([code], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h6 className="text-lg font-semibold text-foreground">
          {languageLabel} Code Preview
        </h6>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
          >
            Copy to Clipboard
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleDownload}
          >
            Download
          </Button>
        </div>
      </div>
      
      {/* Code Block with Syntax Highlighting */}
      <div className="rounded-lg border border-border overflow-hidden">
        <SyntaxHighlighter
          language={syntaxLanguage}
          style={syntaxTheme}
          customStyle={{
            margin: 0,
            fontSize: '0.875rem',
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto',
          }}
          showLineNumbers={true}
          wrapLines={true}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}

